/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { WbsSyncService, WBSItem, WBSField } from '../services/wbs-sync.service';
import { UntypedFormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import * as strings from '@touchplan/strings';
import { ISchedulingDialogInput, ProjectSchedulingComponent } from '../project-scheduling.component';
import { Subject } from 'rxjs';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';
import { UploadOutputFormat } from 'src/app/shared/ui/material/file-upload.component';

/* Test format
WBS Code	TouchPlan Phase	WBS Name	Start	Finish
1	Green	Sitework	2020-01-17	2020-03-17
*/

@Component({
	selector: 'app-wbs-sync',
	templateUrl: './wbs-sync.component.html',
	styleUrls: ['./wbs-sync.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('WbsSyncComponent')],
})
export class WbsSyncComponent implements OnDestroy {
	@Input() public data: ISchedulingDialogInput;
	@Input() public hostDialogRef: MatDialogRef<ProjectSchedulingComponent>;

	public strings = strings;
	public UploadOutputFormat = UploadOutputFormat;
	public form = new UntypedFormControl('', [
		Validators.required,

		// validate the file is valid csv file for WBS sync
		(control: AbstractControl): ValidationErrors | null => {
			let valid = !!control.value;
			if (valid) {
				try {
					this.wbsSyncService.tsvToJson(control.value);
				} catch (e) {
					this.logger.error(e);
					valid = false;
				}
			}
			return valid ? null : ({ dataFormatError: true } as ValidationErrors);
		},
	]);

	private shutdownSubject$ = new Subject<void>();
	private readonly DataFormatError = 'dataFormatError';
	public readonly ValidationData = [
		{ errorCode: this.DataFormatError, message: strings.WBS_CSV_FILE_CONTENT_INVALID },
	];

	public demoTable: WBSItem[] = [
		{
			wbsCode: 1,
			touchplanPhase: strings.WBS_ACCEPTABLE_PHASE,
			wbsName: strings.WBS_DEMO_TABLE_NAME_1,
			startDate: '2020-01-17',
			finishDate: '2020-03-17',
		},
		{
			wbsCode: 2,
			touchplanPhase: strings.WBS_ACCEPTABLE_PHASE,
			wbsName: strings.WBS_DEMO_TABLE_NAME_2,
			startDate: '2020-03-17',
			finishDate: '2020-07-03',
		},
	];
	public demoTableColumns = Object.values(WBSField);

	constructor(
		private wbsSyncService: WbsSyncService,
		private dialog: MatDialog,
		private featureFlagManager: FeatureFlagManager,
		private logger: DefaultLoggerService
	) {}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public save() {
		let success = true;
		try {
			const wbsData = this.wbsSyncService.tsvToJson(this.form.value);
			this.wbsSyncService.saveWbsData(wbsData, this.data.plansApi);
		} catch (e) {
			success = false;
			Logging.warning(e);
		}

		this.showSyncResultDialog(success);
	}

	public onClose() {
		this.hostDialogRef.close();
	}

	private showSyncResultDialog(success: boolean): void {
		this.dialog.open(GenericDialogComponent, {
			data: {
				title: success ? this.strings.WBS_SYNC_SUCCESSFUL : this.strings.WBS_SYNC_FAILED,
				yesButtonText: this.strings.UICONTROL_TEXT_OK,
			},
			disableClose: true,
		});
	}
}
